import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { SectionHeading, SectionLabel } from "./Section";

const SHOTS = [
    {
        key: "ceo",
        label: "CEO Command Center",
        desc: "Your entire company as one conversation — a live Money Meter, decision cards, and every department at a glance.",
        img: "/screen-ceo.png",
    },
    {
        key: "employee",
        label: "Employee Workspace",
        desc: "Teams submit structured updates in seconds. Every entry becomes clean, CEO-ready data automatically.",
        img: "/screen-employee.png",
    },
    {
        key: "admin",
        label: "Company Admin",
        desc: "Provision people, departments, roles and permissions — control the whole workspace from one portal.",
        img: "/screen-admin.png",
    },
];

const PLANS = [
    {
        key: "monthly",
        badge: "Monthly",
        price: "$19",
        period: "/mo",
        note: "Billed monthly · cancel anytime",
        highlight: false,
    },
    {
        key: "yearly",
        badge: "Yearly",
        price: "$199",
        period: "/yr",
        note: "Save ~13% · 2 months free",
        highlight: true,
    },
];

const FEATURES = [
    "Full CEO Command Center",
    "CEO Money Meter forecasts",
    "All 10 AI agents, always-on",
    "Role-based employee workspaces",
    "Business Scan AI",
];

export default function Access() {
    const [showSoon, setShowSoon] = useState(false);
    const open = () => setShowSoon(true);

    return (
        <section
            id="subscribe"
            data-testid="subscribe-section"
            className="relative py-28 sm:py-36"
        >
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="reveal max-w-3xl">
                    <SectionLabel num="08">Subscribe</SectionLabel>
                    <SectionHeading className="mt-6">
                        One command center for your{" "}
                        <span className="italic font-normal">entire company</span>.
                    </SectionHeading>
                    <p className="mt-5 text-[15px] sm:text-lg text-neutral-600 leading-relaxed">
                        CEO’s GPT gives founders, teams, and AI agents a single
                        conversational operating system. Pick a plan and turn every
                        insight into an action.
                    </p>
                </div>

                {/* Screenshots — big, one-by-one on scroll */}
                <div className="mt-20 space-y-28 sm:space-y-40">
                    {SHOTS.map((s, i) => (
                        <motion.div
                            key={s.key}
                            data-testid={`subscribe-shot-${s.key}`}
                            initial={{ opacity: 0, y: 60 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.25 }}
                            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                            className="relative mx-auto max-w-5xl"
                        >
                            <div className="flex items-start gap-4 sm:gap-5 mb-6 sm:mb-8">
                                <span className="font-display text-3xl sm:text-4xl font-light text-violet-300 leading-none shrink-0">
                                    0{i + 1}
                                </span>
                                <div>
                                    <div className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-black">
                                        {s.label}
                                    </div>
                                    <p className="mt-2 text-[14px] sm:text-lg text-neutral-600 leading-relaxed max-w-2xl">
                                        {s.desc}
                                    </p>
                                </div>
                            </div>

                            <button
                                onClick={open}
                                data-testid={`subscribe-shot-btn-${s.key}`}
                                className="group relative block w-full rounded-[24px] sm:rounded-[28px] overflow-hidden glass-strong ring-1 ring-black/[0.06] transition-all duration-500 hover:shadow-[0_50px_120px_-40px_rgba(139,92,246,0.55)]"
                            >
                                {/* browser chrome */}
                                <div className="flex items-center gap-2 px-5 py-3.5 border-b border-black/5 bg-white/60">
                                    <span className="h-3 w-3 rounded-full bg-neutral-200" />
                                    <span className="h-3 w-3 rounded-full bg-neutral-200" />
                                    <span className="h-3 w-3 rounded-full bg-neutral-200" />
                                    <span className="ml-4 inline-flex items-center gap-2 text-[11px] text-neutral-400 font-medium">
                                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                        app.ceosgpt.ai
                                    </span>
                                </div>
                                <div className="relative overflow-hidden bg-neutral-50">
                                    <img
                                        src={s.img}
                                        alt={s.label}
                                        loading="lazy"
                                        className="w-full block object-cover object-top transition-transform duration-[900ms] ease-out group-hover:scale-[1.02]"
                                    />
                                    {/* preview overlay */}
                                    <div className="pointer-events-none absolute inset-0 flex items-end justify-end p-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                        <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold tracking-[0.12em] uppercase text-white bg-black/70 backdrop-blur-md rounded-full px-3.5 py-2">
                                            Preview
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                                <path d="M7 17L17 7M9 7h8v8" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </span>
                                    </div>
                                </div>
                            </button>
                        </motion.div>
                    ))}
                </div>

                {/* Pricing */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
                    {PLANS.map((p, i) => (
                        <div
                            key={p.key}
                            data-testid={`plan-${p.key}`}
                            className={`reveal relative rounded-3xl p-8 sm:p-10 flex flex-col transition-all duration-500 hover:-translate-y-1 ${
                                p.highlight
                                    ? "glass-strong glow-purple ring-1 ring-violet-200"
                                    : "glass hover:shadow-[0_20px_60px_-20px_rgba(139,92,246,0.2)]"
                            }`}
                            style={{ transitionDelay: `${i * 90}ms` }}
                        >
                            <div className="flex items-center justify-between">
                                <span
                                    className={`inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.22em] uppercase px-2.5 py-1 rounded-full ${
                                        p.highlight
                                            ? "bg-violet-600 text-white"
                                            : "bg-neutral-100 text-neutral-700"
                                    }`}
                                >
                                    {p.badge}
                                </span>
                                {p.highlight && (
                                    <span className="text-[10px] font-semibold tracking-[0.22em] uppercase text-violet-600">
                                        Best Value
                                    </span>
                                )}
                            </div>

                            <div className="mt-8 flex items-end gap-1">
                                <span className="font-display text-5xl sm:text-6xl font-medium tracking-tight text-black">
                                    {p.price}
                                </span>
                                <span className="mb-2 text-[15px] text-neutral-500">
                                    {p.period}
                                </span>
                            </div>
                            <p className="mt-2 text-[13px] text-neutral-500">{p.note}</p>

                            <ul className="mt-6 space-y-2.5 text-[13.5px] text-neutral-700">
                                {FEATURES.map((f) => (
                                    <li key={f} className="flex items-center gap-2.5">
                                        <span className={`inline-flex items-center justify-center h-4 w-4 rounded-full ${
                                            p.highlight
                                                ? "bg-violet-600 text-white"
                                                : "bg-neutral-900 text-white"
                                        }`}>
                                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
                                                <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </span>
                                        {f}
                                    </li>
                                ))}
                            </ul>

                            <button
                                data-testid={`subscribe-btn-${p.key}`}
                                onClick={open}
                                className={`mt-8 inline-flex items-center justify-center gap-2 text-sm font-medium px-5 py-3 rounded-full transition-all ${
                                    p.highlight
                                        ? "bg-black text-white hover:shadow-[0_20px_40px_-15px_rgba(139,92,246,0.55)]"
                                        : "bg-white text-black border border-black/10 hover:bg-neutral-50"
                                }`}
                            >
                                Subscribe {p.price}{p.period}
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                    <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Coming soon modal */}
            <AnimatePresence>
                {showSoon && (
                    <div
                        className="fixed inset-0 z-[120] grid place-items-center p-4"
                        data-testid="coming-soon-modal"
                    >
                        <div
                            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
                            onClick={() => setShowSoon(false)}
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.94, y: 12 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.96 }}
                            className="relative w-full max-w-md rounded-[28px] bg-white p-8 text-center shadow-[0_40px_120px_-30px_rgba(139,92,246,0.6)] border border-violet-100"
                        >
                            <div className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-violet-50 text-violet-600">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 8v4l3 2M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div className="mt-5 text-[11px] font-semibold tracking-[0.24em] uppercase text-violet-600">
                                Coming Soon
                            </div>
                            <h3 className="mt-2 font-display text-3xl font-light text-black">
                                Subscriptions are almost here
                            </h3>
                            <p className="mt-3 text-[14px] text-neutral-600 leading-relaxed">
                                We’re putting the finishing touches on CEO’s GPT plans.
                                Try the live demo now — subscriptions open shortly.
                            </p>
                            <button
                                data-testid="coming-soon-close"
                                onClick={() => setShowSoon(false)}
                                className="mt-6 w-full rounded-2xl bg-black text-white font-medium py-3 hover:shadow-[0_16px_40px_-12px_rgba(139,92,246,0.7)] transition-all"
                            >
                                Got it
                            </button>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </section>
    );
}
